import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const DigitalMarketingPage: React.FC = () => {
  const benefits = [
    {
      icon: 'ri-global-line',
      title: 'Multi-Channel Reach',
      description: 'Connect with customers across all digital platforms where they spend their time.'
    },
    {
      icon: 'ri-pie-chart-line',
      title: 'Data-Driven Strategy',
      description: 'Make informed decisions based on real performance data and customer insights.'
    },
    {
      icon: 'ri-rocket-line',
      title: 'Accelerated Growth',
      description: 'Combine multiple marketing channels for exponential business growth.'
    },
    {
      icon: 'ri-team-line',
      title: 'Expert Team',
      description: 'Access to specialists in SEO, PPC, social media, and content marketing.'
    }
  ];

  const features = [
    'Comprehensive marketing strategy',
    'SEO & local search optimization',
    'PPC advertising management',
    'Social media marketing',
    'Content creation & marketing',
    'Email marketing campaigns',
    'Analytics & performance tracking',
    'Monthly strategy consultations'
  ];

  const services = [
    { name: 'Search Engine Optimization', icon: 'ri-search-line', color: 'text-green-600' },
    { name: 'Pay-Per-Click Advertising', icon: 'ri-advertisement-line', color: 'text-blue-600' },
    { name: 'Social Media Marketing', icon: 'ri-share-line', color: 'text-pink-600' },
    { name: 'Content Marketing', icon: 'ri-article-line', color: 'text-purple-600' },
    { name: 'Email Marketing', icon: 'ri-mail-line', color: 'text-orange-600' },
    { name: 'Analytics & Reporting', icon: 'ri-bar-chart-line', color: 'text-indigo-600' }
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center bg-gradient-to-r from-purple-100 to-pink-100 rounded-full px-6 py-2 mb-6">
            <i className="ri-rocket-line text-purple-600 mr-2"></i>
            <span className="text-purple-800 font-medium">Digital Marketing Services</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-purple-600 via-pink-600 to-red-600 bg-clip-text text-transparent">
              Complete Digital
            </span>
            <br />
            Marketing Solutions
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Comprehensive digital marketing strategies that combine multiple channels to maximize your online presence and drive sustainable business growth.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-white text-pink-600 border-2 border-pink-600 px-8 py-4 rounded-full font-semibold hover:bg-pink-50 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Why Small Businesses Need Digital Marketing
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              In today's digital world, a comprehensive marketing strategy is essential to compete and grow your business effectively.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Integrated Marketing Services
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We combine the best digital marketing channels to create a cohesive strategy that drives results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className={`${service.icon} text-3xl ${service.color}`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-800">{service.name}</h3>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h3 className="text-3xl font-bold mb-6">
                <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Comprehensive Marketing Strategy
                </span>
              </h3>
              <p className="text-xl text-gray-600 mb-8">
                Our integrated approach ensures all your marketing efforts work together to maximize ROI and drive sustainable growth.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-pink-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/54d5362bc71ce7db55f74c650cb5fbb3.jpg"
                alt="Digital Marketing Strategy"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-pink-50 to-red-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-pink-600 to-red-600 bg-clip-text text-transparent">
                Digital Marketing Results
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">250%</div>
              <p className="text-gray-600">Average traffic increase</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-pink-600 mb-2">180%</div>
              <p className="text-gray-600">Lead generation improvement</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-red-600 mb-2">320%</div>
              <p className="text-gray-600">ROI improvement</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">90%</div>
              <p className="text-gray-600">Client retention rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-purple-600 via-pink-600 to-red-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Transform Your Digital Presence?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let our digital marketing experts create a comprehensive strategy that drives real results for your business.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/consultation"
              className="bg-white text-pink-600 px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-pink-600 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>

          {/* Social Media Links */}
          <div className="flex justify-center space-x-6">
            <a href="#" className="text-white hover:text-purple-200 transition-colors duration-300">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-purple-200 transition-colors duration-300">
              <i className="ri-instagram-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-purple-200 transition-colors duration-300">
              <i className="ri-twitter-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-purple-200 transition-colors duration-300">
              <i className="ri-linkedin-fill text-2xl"></i>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default DigitalMarketingPage;
